import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Modal, Button } from './primitives';
import { useCompanies, useEmailRecipients, useForgetEmailRecipient } from '../api/hooks';
import type { Company } from '../api/schemas';
import styles from './EmailRecipientsInput.module.css';
import { cx } from '../lib/cx';

/**
 * Pole pro e-mailové adresy (čárkou oddělené) s našeptávačem.
 *
 * Zdroje návrhů:
 *  1. historie — adresy, na které uživatel už jednou poslal (server,
 *     /api/email/recipients; ukládá se při úspěšném odeslání, takže to funguje
 *     napříč telefonem, tabletem i PC),
 *  2. společný firemní seznam pro doménu účtu (např. všichni @kvs-stavebni.cz),
 *  3. adresář firem — kontaktní osoby a e-maily firem.
 *
 * Napíšeš pár písmen → nabídka pod polem → klik (nebo Enter/Tab) dopíše adresu
 * a čárku. Tlačítko 👤 otevře celý seznam kontaktů s e-mailem.
 */

export interface EmailSuggestion {
  email: string;
  /** Jméno osoby / firma — pro zobrazení a hledání. */
  label: string;
  source: 'history' | 'company' | 'contacts';
  useCount: number;
}

function collectContactEmails(companies: Company[]): EmailSuggestion[] {
  const out: EmailSuggestion[] = [];
  const push = (email: string | undefined, name: string, firma: string) => {
    const e = (email ?? '').trim().toLowerCase();
    if (!e || !e.includes('@')) return;
    const label = [name.trim(), firma.trim()].filter(Boolean).join(' · ');
    out.push({ email: e, label, source: 'contacts', useCount: 0 });
  };
  for (const c of companies) {
    const firma = c.nazev ?? '';
    push(c.kontakt_email, c.kontakt_jmeno ?? '', firma);
    for (const k of c.contacts ?? []) push(k.email, k.jmeno ?? '', firma);
    push(c.fakturacni_email, 'fakturace', firma);
  }
  return out;
}

/** Sloučí historii a adresář; jedna položka na adresu, historie má přednost v řazení. */
export function useEmailSuggestions(enabled = true): EmailSuggestion[] {
  const { data: history = [] } = useEmailRecipients(enabled);
  const { data: companies = [] } = useCompanies();
  return useMemo(() => {
    const byEmail = new Map<string, EmailSuggestion>();
    for (const c of collectContactEmails(companies)) {
      const prev = byEmail.get(c.email);
      if (!prev) byEmail.set(c.email, c);
      else if (!prev.label && c.label) prev.label = c.label;
    }
    for (const h of history) {
      const e = h.email.toLowerCase();
      const prev = byEmail.get(e);
      byEmail.set(e, {
        email: e,
        label: h.name?.trim() || prev?.label || '',
        source: h.use_count > 0 ? 'history' : 'company',
        useCount: h.use_count,
      });
    }
    return [...byEmail.values()].sort(
      (a, b) =>
        b.useCount - a.useCount ||
        (a.label || a.email).localeCompare(b.label || b.email, 'cs'),
    );
  }, [history, companies]);
}

/** Rozdělí hodnotu pole na hotové adresy a právě psaný token (za poslední čárkou). */
function splitValue(value: string): { done: string[]; token: string } {
  const parts = value.split(/[,;]/);
  const token = (parts.pop() ?? '').trim();
  return { done: parts.map((p) => p.trim()).filter(Boolean), token };
}

function joinValue(addrs: string[]): string {
  return addrs.length ? addrs.join(', ') + ', ' : '';
}

function norm(s: string) {
  return s
    .toLocaleLowerCase('cs')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

const MAX_SHOWN = 8;

function sourceLabel(s: EmailSuggestion): string {
  if (s.source === 'history') return '↺ použito';
  if (s.source === 'company') return 'firemní';
  return 'adresář';
}

export interface EmailRecipientsInputProps {
  value: string;
  onChange: (v: string) => void;
  suggestions: EmailSuggestion[];
  placeholder?: string;
  autoFocus?: boolean;
  /** Nadpis dialogu 👤 (např. „Komu", „Kopie (CC)"). */
  pickerTitle?: string;
}

export function EmailRecipientsInput({
  value,
  onChange,
  suggestions,
  placeholder,
  autoFocus,
  pickerTitle = 'Příjemci',
}: EmailRecipientsInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [active, setActive] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState('');
  const forget = useForgetEmailRecipient();

  const { done, token } = splitValue(value);
  const already = useMemo(() => new Set(done.map((d) => d.toLowerCase())), [done]);

  const matches = useMemo(() => {
    const q = norm(token);
    if (!q) return [];
    const starts: EmailSuggestion[] = [];
    const contains: EmailSuggestion[] = [];
    for (const s of suggestions) {
      if (already.has(s.email)) continue;
      if (s.email === q) continue; // už je dopsaná celá
      const em = norm(s.email);
      const lb = norm(s.label);
      if (em.startsWith(q) || lb.startsWith(q) || lb.split(/[\s·]+/).some((w) => w.startsWith(q))) {
        starts.push(s);
      } else if (em.includes(q) || lb.includes(q)) {
        contains.push(s);
      }
    }
    return [...starts, ...contains].slice(0, MAX_SHOWN);
  }, [suggestions, token, already]);

  const showList = focused && !dismissed && matches.length > 0;
  const activeIdx = Math.min(active, Math.max(0, matches.length - 1));

  function accept(email: string) {
    onChange(joinValue([...done, email]));
    setActive(0);
    setDismissed(false);
    // Fokus zůstává v poli → rovnou psát další adresu.
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!showList) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((activeIdx + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((activeIdx - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      accept(matches[activeIdx].email);
    } else if (e.key === 'Escape') {
      // Zavřít jen nabídku, ne celý dialog.
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
      e.stopPropagation();
      setDismissed(true);
    }
  }

  // ── Dialog 👤 — celý seznam ────────────────────────────────────────────
  const pickerList = useMemo(() => {
    const q = norm(pickerQ.trim());
    const list = q
      ? suggestions.filter((s) => norm(s.email).includes(q) || norm(s.label).includes(q))
      : suggestions;
    return list;
  }, [suggestions, pickerQ]);

  function togglePicked(email: string) {
    const current = [...done, ...(token ? [token] : [])];
    const has = current.some((c) => c.toLowerCase() === email);
    const next = has ? current.filter((c) => c.toLowerCase() !== email) : [...current, email];
    onChange(joinValue(next));
  }

  const pickedSet = useMemo(() => {
    const s = new Set(already);
    if (token) s.add(token.toLowerCase());
    return s;
  }, [already, token]);

  return (
    <div className={styles.wrap}>
      <div className={styles.row}>
        <input
          ref={inputRef}
          className={styles.input}
          // type="text" + inputMode: type="email" by prohlížeč „čistil" mezery
          // za čárkou a rozbil psaní další adresy. Klávesnice s @ zůstane.
          type="text"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setActive(0);
            setDismissed(false);
          }}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          // Zpoždění: klik do nabídky musí proběhnout dřív, než se zavře.
          onBlur={() => window.setTimeout(() => setFocused(false), 150)}
          autoFocus={autoFocus}
        />
        <button
          type="button"
          className={styles.pickBtn}
          onClick={() => {
            setPickerQ('');
            setPickerOpen(true);
          }}
          title="Vybrat z kontaktů a dříve použitých adres"
          aria-label="Vybrat z kontaktů"
        >
          👤
        </button>
      </div>

      {showList && (
        <ul className={styles.list} role="listbox">
          {matches.map((m, i) => (
            <li
              key={m.email}
              role="option"
              aria-selected={i === activeIdx}
              className={cx(styles.item, i === activeIdx && styles.itemActive)}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => accept(m.email)}
            >
              <div className={styles.itemMain}>
                <span className={styles.itemEmail}>{m.email}</span>
                {m.label && <span className={styles.itemLabel}>{m.label}</span>}
              </div>
              <span className={styles.itemSource}>
                {sourceLabel(m)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={`Vybrat adresy — ${pickerTitle}`}
        footer={
          <Button variant="primary" onClick={() => setPickerOpen(false)}>
            Hotovo
          </Button>
        }
      >
        <input
          className={styles.search}
          placeholder="Hledat podle jména, firmy, e-mailu…"
          value={pickerQ}
          onChange={(e) => setPickerQ(e.target.value)}
        />
        {pickerList.length === 0 ? (
          <div className={styles.empty}>
            {suggestions.length === 0
              ? 'Zatím žádné adresy. Uloží se samy po prvním odeslání, nebo je doplňte do adresáře firem.'
              : `Žádný výsledek pro „${pickerQ.trim()}".`}
          </div>
        ) : (
          <div className={styles.pickList}>
            {pickerList.map((s) => {
              const on = pickedSet.has(s.email);
              return (
                <div key={s.email} className={cx(styles.pickItem, on && styles.pickItemOn)}>
                  <button
                    type="button"
                    className={styles.pickMain}
                    onClick={() => togglePicked(s.email)}
                  >
                    <span className={styles.check} aria-hidden>
                      {on ? '✓' : ''}
                    </span>
                    <span className={styles.itemMain}>
                      <span className={styles.itemEmail}>{s.email}</span>
                      {s.label && <span className={styles.itemLabel}>{s.label}</span>}
                    </span>
                    <span className={styles.itemSource}>
                      {s.source === 'history' ? `↺ ${s.useCount}×` : sourceLabel(s)}
                    </span>
                  </button>
                  {s.source === 'history' && (
                    <button
                      type="button"
                      className={styles.forget}
                      title="Odebrat z historie (adresář firem zůstane beze změny)"
                      aria-label={`Odebrat ${s.email} z historie`}
                      disabled={forget.isPending}
                      onClick={() => forget.mutate(s.email)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {forget.isError && (
          <div className={styles.err}>
            Odebrání z historie se nepovedlo
            {forget.error instanceof Error ? `: ${forget.error.message}` : '.'}
          </div>
        )}
      </Modal>
    </div>
  );
}
